import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_URL } from '../config/api';
import ConfirmModal from './common/ConfirmModal';
import './CouponManager.css';

const CouponManager = () => {
  const [coupons, setCoupons] = useState([]);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [filters, setFilters] = useState({
    isActive: '',
    search: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    discountType: 'percentage',
    discountValue: '',
    minPurchaseAmount: '',
    maxDiscountAmount: '',
    validFrom: '',
    validUntil: '',
    usageLimit: '',
    perUserLimit: 1,
    applicableTo: 'all',
    categories: [],
    products: [],
    customerEmails: [],
    isActive: true
  });
  const [confirmModal, setConfirmModal] = useState({
    show: false,
    title: '',
    message: '',
    onConfirm: null,
    type: 'danger'
  });

  useEffect(() => {
    fetchCoupons();
    fetchCategories();
    fetchProducts();
  }, [filters, pagination.page]);

  const fetchCoupons = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit
      });
      if (filters.isActive) params.append('isActive', filters.isActive);
      if (filters.search) params.append('search', filters.search);

      const response = await axios.get(`${API_URL}/api/coupons?${params.toString()}`);
      setCoupons(response.data.data);
      setPagination({
        ...pagination,
        total: response.data.total,
        totalPages: response.data.totalPages
      });
    } catch (error) {
      toast.error('Failed to fetch coupons');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/categories`);
      setCategories(response.data.data);
    } catch (error) {
      console.error('Failed to fetch categories');
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/products?limit=100`);
      setProducts(response.data.data);
    } catch (error) {
      console.error('Failed to fetch products');
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleArrayChange = (field, value) => {
    setFormData({
      ...formData,
      [field]: value
    });
  };

  const handleSubmit = async (e) => {
    // Prevent any default form behavior and event bubbling
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication required. Please login again.');
        return false;
      }
      
      const submitData = {
        ...formData,
        discountValue: parseFloat(formData.discountValue),
        minPurchaseAmount: parseFloat(formData.minPurchaseAmount) || 0,
        maxDiscountAmount: formData.maxDiscountAmount ? parseFloat(formData.maxDiscountAmount) : undefined,
        usageLimit: formData.usageLimit ? parseInt(formData.usageLimit) : undefined,
        perUserLimit: parseInt(formData.perUserLimit) || 1,
        validFrom: new Date(formData.validFrom),
        validUntil: new Date(formData.validUntil),
        customerEmails: formData.customerEmails.filter(email => email.trim())
      };

      if (editingCoupon) {
        await axios.put(`${API_URL}/api/coupons/${editingCoupon._id}`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Coupon updated successfully');
      } else {
        await axios.post(`${API_URL}/api/coupons`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Coupon created successfully');
      }

      setShowModal(false);
      resetForm();
      fetchCoupons();
    } catch (error) {
      console.error('Coupon submit error:', error);
      // Handle authentication errors specifically
      if (error.response?.status === 401) {
        toast.error('Authentication failed. Please login again.');
      } else {
        toast.error(error.response?.data?.message || 'Failed to save coupon');
      }
      // Don't close modal on error - keep form data so user can fix and retry
      return false;
    }
  };

  const handleEdit = (coupon) => {
    setEditingCoupon(coupon);
    setFormData({
      code: coupon.code,
      name: coupon.name,
      description: coupon.description || '',
      discountType: coupon.discountType,
      discountValue: coupon.discountValue,
      minPurchaseAmount: coupon.minPurchaseAmount || '',
      maxDiscountAmount: coupon.maxDiscountAmount || '',
      validFrom: new Date(coupon.validFrom).toISOString().split('T')[0],
      validUntil: new Date(coupon.validUntil).toISOString().split('T')[0],
      usageLimit: coupon.usageLimit || '',
      perUserLimit: coupon.perUserLimit || 1,
      applicableTo: coupon.applicableTo,
      categories: coupon.categories.map(c => c._id || c),
      products: coupon.products.map(p => p._id || p),
      customerEmails: coupon.customerEmails || [],
      isActive: coupon.isActive
    });
    setShowModal(true);
  };

  const handleDelete = (coupon) => {
    setConfirmModal({
      show: true,
      title: 'Delete Coupon',
      message: `Are you sure you want to delete coupon "${coupon.code}"? This action cannot be undone.`,
      type: 'danger',
      onConfirm: async () => {
        try {
          await axios.delete(`${API_URL}/api/coupons/${coupon._id}`);
          toast.success('Coupon deleted successfully');
          fetchCoupons();
        } catch (error) {
          toast.error('Failed to delete coupon');
        }
        setConfirmModal({ ...confirmModal, show: false });
      }
    });
  };

  const resetForm = () => {
    setFormData({
      code: '',
      name: '',
      description: '',
      discountType: 'percentage',
      discountValue: '',
      minPurchaseAmount: '',
      maxDiscountAmount: '',
      validFrom: '',
      validUntil: '',
      usageLimit: '',
      perUserLimit: 1,
      applicableTo: 'all',
      categories: [],
      products: [],
      customerEmails: [],
      isActive: true
    });
    setEditingCoupon(null);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const isExpired = (validUntil) => {
    return new Date(validUntil) < new Date();
  };

  const isUpcoming = (validFrom) => {
    return new Date(validFrom) > new Date();
  };

  return (
    <div className="coupon-manager">
      <div className="coupon-manager-header">
        <h1>Coupon Manager</h1>
        <button className="btn btn-primary" onClick={() => { resetForm(); setShowModal(true); }}>
          + Add Coupon
        </button>
      </div>

      {/* Filters */}
      <div className="coupon-filters">
        <input
          type="text"
          placeholder="Search coupons..."
          value={filters.search}
          onChange={(e) => setFilters({ ...filters, search: e.target.value, page: 1 })}
          className="filter-input"
        />
        <select
          value={filters.isActive}
          onChange={(e) => setFilters({ ...filters, isActive: e.target.value, page: 1 })}
          className="filter-select"
        >
          <option value="">All Status</option>
          <option value="true">Active</option>
          <option value="false">Inactive</option>
        </select>
      </div>

      {/* Coupons Table */}
      {loading ? (
        <div className="loading">Loading coupons...</div>
      ) : (
        <>
          <table className="coupon-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Name</th>
                <th>Discount</th>
                <th>Valid Period</th>
                <th>Usage</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {coupons.length === 0 ? (
                <tr>
                  <td colSpan="7" className="no-data">No coupons found</td>
                </tr>
              ) : (
                coupons.map((coupon) => (
                  <tr key={coupon._id}>
                    <td><strong>{coupon.code}</strong></td>
                    <td>{coupon.name}</td>
                    <td>
                      {coupon.discountType === 'percentage' 
                        ? `${coupon.discountValue}%` 
                        : `₹${coupon.discountValue}`}
                      {coupon.minPurchaseAmount > 0 && (
                        <div className="text-muted">Min: ₹{coupon.minPurchaseAmount}</div>
                      )}
                    </td>
                    <td>
                      <div>{formatDate(coupon.validFrom)} - {formatDate(coupon.validUntil)}</div>
                      {isExpired(coupon.validUntil) && (
                        <span className="badge badge-danger">Expired</span>
                      )}
                      {isUpcoming(coupon.validFrom) && (
                        <span className="badge badge-warning">Upcoming</span>
                      )}
                    </td>
                    <td>
                      {coupon.usageCount || 0} / {coupon.usageLimit || '∞'}
                    </td>
                    <td>
                      <span className={`badge ${coupon.isActive ? 'badge-success' : 'badge-secondary'}`}>
                        {coupon.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => handleEdit(coupon)}
                      >
                        Edit
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDelete(coupon)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
                disabled={pagination.page === 1}
              >
                Previous
              </button>
              <span>Page {pagination.page} of {pagination.totalPages}</span>
              <button
                onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
                disabled={pagination.page >= pagination.totalPages}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => { setShowModal(false); resetForm(); }}>
          <div className="modal-content coupon-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingCoupon ? 'Edit Coupon' : 'Create Coupon'}</h2>
              <button className="modal-close" onClick={() => { setShowModal(false); resetForm(); }}>×</button>
            </div>

            <form 
              onSubmit={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleSubmit(e);
              }} 
              className="coupon-form"
              noValidate
            >
              <div className="form-row">
                <div className="form-group">
                  <label>Coupon Code *</label>
                  <input
                    type="text"
                    name="code"
                    value={formData.code}
                    onChange={handleInputChange}
                    required
                    disabled={!!editingCoupon}
                    placeholder="SAVE20"
                    pattern="[A-Z0-9]+"
                    title="Only uppercase letters and numbers"
                  />
                  <small>Uppercase letters and numbers only</small>
                </div>

                <div className="form-group">
                  <label>Coupon Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="Summer Sale 20% Off"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="2"
                  placeholder="Coupon description..."
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Discount Type *</label>
                  <select
                    name="discountType"
                    value={formData.discountType}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="percentage">Percentage</option>
                    <option value="fixed">Fixed Amount</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Discount Value *</label>
                  <input
                    type="number"
                    name="discountValue"
                    value={formData.discountValue}
                    onChange={handleInputChange}
                    required
                    min="0"
                    step="0.01"
                    placeholder={formData.discountType === 'percentage' ? '20' : '100'}
                  />
                  {formData.discountType === 'percentage' && formData.discountValue > 100 && (
                    <small className="text-danger">Percentage cannot exceed 100%</small>
                  )}
                </div>
              </div>

              {formData.discountType === 'percentage' && (
                <div className="form-group">
                  <label>Max Discount Amount (Optional)</label>
                  <input
                    type="number"
                    name="maxDiscountAmount"
                    value={formData.maxDiscountAmount}
                    onChange={handleInputChange}
                    min="0"
                    step="0.01"
                    placeholder="500"
                  />
                  <small>Maximum discount amount for percentage discounts</small>
                </div>
              )}

              <div className="form-group">
                <label>Minimum Purchase Amount</label>
                <input
                  type="number"
                  name="minPurchaseAmount"
                  value={formData.minPurchaseAmount}
                  onChange={handleInputChange}
                  min="0"
                  step="0.01"
                  placeholder="1000"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Valid From *</label>
                  <input
                    type="date"
                    name="validFrom"
                    value={formData.validFrom}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Valid Until *</label>
                  <input
                    type="date"
                    name="validUntil"
                    value={formData.validUntil}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Usage Limit (Optional)</label>
                  <input
                    type="number"
                    name="usageLimit"
                    value={formData.usageLimit}
                    onChange={handleInputChange}
                    min="1"
                    placeholder="100"
                  />
                  <small>Total number of times coupon can be used</small>
                </div>

                <div className="form-group">
                  <label>Per User Limit</label>
                  <input
                    type="number"
                    name="perUserLimit"
                    value={formData.perUserLimit}
                    onChange={handleInputChange}
                    min="1"
                  />
                  <small>How many times a single user can use this coupon</small>
                </div>
              </div>

              <div className="form-group">
                <label>Applicable To *</label>
                <select
                  name="applicableTo"
                  value={formData.applicableTo}
                  onChange={handleInputChange}
                  required
                >
                  <option value="all">All Products</option>
                  <option value="categories">Specific Categories</option>
                  <option value="products">Specific Products</option>
                  <option value="customers">Specific Customers</option>
                </select>
              </div>

              {formData.applicableTo === 'categories' && (
                <div className="form-group">
                  <label>Select Categories</label>
                  <select
                    multiple
                    value={formData.categories}
                    onChange={(e) => {
                      const selected = Array.from(e.target.selectedOptions, option => option.value);
                      handleArrayChange('categories', selected);
                    }}
                    className="multi-select"
                  >
                    {categories.map(cat => (
                      <option key={cat._id} value={cat._id}>{cat.name}</option>
                    ))}
                  </select>
                  <small>Hold Ctrl/Cmd to select multiple</small>
                </div>
              )}

              {formData.applicableTo === 'products' && (
                <div className="form-group">
                  <label>Select Products</label>
                  <select
                    multiple
                    value={formData.products}
                    onChange={(e) => {
                      const selected = Array.from(e.target.selectedOptions, option => option.value);
                      handleArrayChange('products', selected);
                    }}
                    className="multi-select"
                  >
                    {products.map(prod => (
                      <option key={prod._id} value={prod._id}>{prod.name}</option>
                    ))}
                  </select>
                  <small>Hold Ctrl/Cmd to select multiple</small>
                </div>
              )}

              {formData.applicableTo === 'customers' && (
                <div className="form-group">
                  <label>Customer Emails (One per line)</label>
                  <textarea
                    value={formData.customerEmails.join('\n')}
                    onChange={(e) => {
                      const emails = e.target.value.split('\n').map(email => email.trim()).filter(email => email);
                      handleArrayChange('customerEmails', emails);
                    }}
                    rows="5"
                    placeholder="customer1@example.com&#10;customer2@example.com"
                  />
                </div>
              )}

              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleInputChange}
                  />
                  Active
                </label>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => { setShowModal(false); resetForm(); }}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingCoupon ? 'Update' : 'Create'} Coupon
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmModal
        show={confirmModal.show}
        title={confirmModal.title}
        message={confirmModal.message}
        type={confirmModal.type}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal({ ...confirmModal, show: false })}
      />
    </div>
  );
};

export default CouponManager;
